import { FeedbacksReportsResponse, FeedbackWeekData, FeedbackWeekStats, FeedbackReportItem } from '../types/reports';
import { startOfWeek, format, parseISO, addDays } from 'date-fns';

// Mapping of feedback field keys to human-readable labels
export const SESSION_FEEDBACK_LABELS: Record<string, string> = {
  welcomeMessageRating: 'Welcome Message',
  wellbeingCheckRating: 'Wellbeing Check-in',
  understandingAdviceRating: 'Understanding & Advice',
  websiteReferralRating: 'Website Referral',
  adviceRelevanceRating: 'Advice Relevance',
  responseTimingRating: 'Response Timing',
  officerReferralRating: 'Officer Referral',
  conversationStyleRating: 'Conversation Style',
  adaptabilityRating: 'Adaptability',
  helpfulnessRating: 'Helpfulness',
  overallRating: 'Overall Rating'
};

export const APP_FEEDBACK_LABELS: Record<string, string> = {
  ratingFriends: 'Friend Rating',
  ratingCustomers: 'Customer Rating'
};

// Extract numeric rating fields from feedback data
export const getNumericRatingFields = (feedbackData: any, type: 'session' | 'app' | 'other'): Record<string, number> => {
  const fields: Record<string, number> = {};
  
  if (type === 'session') {
    const labels = SESSION_FEEDBACK_LABELS;
    Object.keys(labels).forEach(key => {
      const value = feedbackData[key];
      if (typeof value === 'number' && value > 0) {
        fields[key] = value;
      }
    });
  } else if (type === 'app') {
    const labels = APP_FEEDBACK_LABELS;
    Object.keys(labels).forEach(key => {
      const value = feedbackData[key];
      if (typeof value === 'number' && value > 0) {
        fields[key] = value;
      }
    });
  }
  // For 'other' type, we don't extract any specific fields since we don't know the structure
  
  return fields;
};

// Group feedbacks by week starting from Monday
export const groupFeedbacksByWeek = (feedbacks: FeedbackReportItem[]): Map<string, FeedbackReportItem[]> => {
  const weekGroups = new Map<string, FeedbackReportItem[]>();
  
  feedbacks.forEach(feedback => {
    const date = parseISO(feedback.createdAt);
    const weekStart = startOfWeek(date, { weekStartsOn: 1 }); // Monday = 1
    const weekKey = format(weekStart, 'yyyy-MM-dd');
    
    if (!weekGroups.has(weekKey)) {
      weekGroups.set(weekKey, []);
    }
    weekGroups.get(weekKey)!.push(feedback);
  });
  
  return weekGroups;
};

// Calculate weekly stats for a specific feedback type
export const calculateWeeklyStats = (
  weeklyFeedbacks: Map<string, FeedbackReportItem[]>,
  type: 'session' | 'app' | 'other'
): FeedbackWeekData[] => {
  const weekData: FeedbackWeekData[] = [];
  const labels = type === 'session' ? SESSION_FEEDBACK_LABELS : (type === 'app' ? APP_FEEDBACK_LABELS : {});
  
  // Sort weeks chronologically
  const sortedWeeks = Array.from(weeklyFeedbacks.keys()).sort();
  
  sortedWeeks.forEach(weekStartStr => {
    const feedbacks = weeklyFeedbacks.get(weekStartStr) || [];
    const weekStart = parseISO(weekStartStr);
    const weekEnd = addDays(weekStart, 6);
    
    // Filter feedbacks by type for this week
    const typeFeedbacks = feedbacks.filter(f => f.type === type);
    
    if (typeFeedbacks.length === 0) return;
    
    const statsMap = new Map<string, { values: number[]; label: string }>();
    
    // Initialize stats for all possible fields
    Object.keys(labels).forEach(key => {
      statsMap.set(key, { values: [], label: labels[key] });
    });
    
    // Collect values for each field
    typeFeedbacks.forEach(feedback => {
      const ratingFields = getNumericRatingFields(feedback.feedbackData, type);
      Object.entries(ratingFields).forEach(([key, value]) => {
        if (statsMap.has(key)) {
          statsMap.get(key)!.values.push(value);
        }
      });
    });
    
    // Calculate statistics
    const weekStats: FeedbackWeekStats[] = [];
    statsMap.forEach((data, key) => {
      if (data.values.length > 0) {
        const total = data.values.reduce((sum, val) => sum + val, 0);
        const count = data.values.length;
        const average = total / count;
        
        weekStats.push({
          key,
          values: data.values,
          total,
          count,
          average,
          label: data.label
        });
      }
    });
    
    const weekDataItem: FeedbackWeekData = {
      weekStartDate: format(weekStart, 'yyyy-MM-dd'),
      weekEndDate: format(weekEnd, 'yyyy-MM-dd'),
      sessionFeedbacks: type === 'session' ? weekStats : [],
      appFeedbacks: type === 'app' ? weekStats : []
    };
    
    weekData.push(weekDataItem);
  });
  
  return weekData;
};

// Process feedback reports into weekly aggregated data
export const processFeedbacksToWeekly = (response: FeedbacksReportsResponse): {
  sessionWeeks: FeedbackWeekData[];
  appWeeks: FeedbackWeekData[];
} => {
  const weeklyFeedbacks = groupFeedbacksByWeek(response.feedbacks);
  
  const sessionWeeks = calculateWeeklyStats(weeklyFeedbacks, 'session');
  const appWeeks = calculateWeeklyStats(weeklyFeedbacks, 'app');
  
  return { sessionWeeks, appWeeks };
};

// Get unique weeks that have data for either type
export const getAllWeeksWithData = (sessionWeeks: FeedbackWeekData[], appWeeks: FeedbackWeekData[]): string[] => {
  const allWeeks = new Set<string>();
  
  sessionWeeks.forEach(week => allWeeks.add(week.weekStartDate));
  appWeeks.forEach(week => allWeeks.add(week.weekStartDate));
  
  return Array.from(allWeeks).sort();
};